/**
 * StepConfirmFree — used when the selected service is a Free Consult (€0).
 * Submits the booking directly without a payment step.
 */
import { useEffect, useRef, useState } from "react";
import { Hourglass, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { isClinic } from "../../lib/site";
import { useBooking } from "../../lib/BookingContext";
import { navigateWithAttribution } from "../../lib/analytics";

const FREE_FALLBACK_THANK_YOU = {
  dental: "https://www.whitesmiledental.ie/book-online-thank-you/",
  clinic: "https://whitesmileclinic.ie/bookly-confirmation-thank-you/",
};

export default function StepConfirmFree() {
  const ctx = useBooking();
  const { service, location, slot, slotClinicianId, patient, bookingId, back, update } = ctx;
  const [status, setStatus] = useState(bookingId ? "done" : "submitting");
  const [thankYouUrl, setThankYouUrl] = useState(null);
  const submitted = useRef(false);

  // Submit on mount (only once)
  useEffect(() => {
    if (submitted.current || bookingId) return;
    submitted.current = true;
    (async () => {
      try {
        const fullPhone = patient.phone_country + patient.phone;
        const res = await api.createBooking({
          practice_id: location.id,
          service_id: service.id,
          clinician_id: slotClinicianId ? String(slotClinicianId) : null,
          appointment_time: slot,
          first_name: patient.first_name.trim(),
          last_name: patient.last_name.trim(),
          email: patient.email.trim() || `noemail+${Date.now()}@whitesmiledental.ie`,
          phone: fullPhone,
          notes: patient.notes || "",
          pay_online: false,
          aerona_patient_id: patient.aerona_patient_id != null ? String(patient.aerona_patient_id) : null,
          force_new_patient: !!patient.force_new_patient,
          origin_url: window.location.origin,
        });
        update({ bookingId: res.booking_id });
        sessionStorage.setItem("ws_last_booking", res.booking_id);
        // Capture the authoritative per-site thank-you URL returned by the
        // backend so the follow-up redirect can't be fooled by a stale
        // client-side isClinic() reading.
        if (res.thank_you_url) setThankYouUrl(res.thank_you_url);
        setStatus("done");
      } catch (e) {
        if (e?.response?.status === 409) {
          ctx.setConflict(e.response.data?.detail || "Apologies, someone else just booked that time slot right now. Please pick another time.");
          return;
        }
        toast.error("Couldn't confirm your booking. Please try again.");
        setStatus("error");
      }
    })();
     
  }, []);

  // Once Aerona has the appointment, redirect almost immediately so the
  // WordPress thank-you page can fire the Meta/Google custom conversions.
  // Use navigateWithAttribution so UTMs and the GA4 `_gl` linker param
  // ride along for cross-domain session continuity.
  useEffect(() => {
    if (status !== "done") return;
    const t = setTimeout(() => {
      navigateWithAttribution(thankYouUrl || FREE_FALLBACK_THANK_YOU[isClinic() ? "clinic" : "dental"]);
    }, 500);
    return () => clearTimeout(t);
  }, [status, thankYouUrl]);

  // Safety net: never leave the patient stuck on the spinner — even if the
  // Aerona POST is mid-flight, fire the redirect after 6 s. Aerona itself
  // serialises the appointment so duplicates won't be created.
  useEffect(() => {
    if (status === "error") return;
    const fallback = setTimeout(() => {
      navigateWithAttribution(thankYouUrl || FREE_FALLBACK_THANK_YOU[isClinic() ? "clinic" : "dental"]);
    }, 6000);
    return () => clearTimeout(fallback);
  }, [status, thankYouUrl]);

  return (
    <div className="text-center py-6" data-testid="step-done">
      {status === "error" ? (
        <>
          <p className="text-[#D0021B] mb-6">Something went wrong. Please try again.</p>
          <button onClick={back} className="ws-btn-outline inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" strokeWidth={2} /> Back
          </button>
        </>
      ) : (
        <>
          <div className="w-16 h-16 mx-auto rounded-full bg-[#E8F0FE] grid place-items-center mb-4">
            <Hourglass className="w-8 h-8 text-[#0466ff] animate-pulse" strokeWidth={1.5} />
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold mb-3" style={{ fontFamily: "Manrope" }}>
            Please wait while we confirm your booking…
          </h2>
          <div className="flex items-center justify-center text-[#5C5C5C]" data-testid="success-loading">
            <Loader2 className="w-5 h-5 animate-spin text-[#0466ff]" strokeWidth={2} />
          </div>
        </>
      )}
    </div>
  );
}
